import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Badge, ButtonGroup } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import SearchInputWithClear from '../components/SearchInputWithClear'
import { usePermissions } from '../contexts/PermissionContext'

function ProductionListScreen() {
    const { hasPermission } = usePermissions()
    const [productionLots, setProductionLots] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [statusFilter, setStatusFilter] = useState('in_progress')
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('productionColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            lot_number: 150,
            recipe_name: 200,
            status: 120,
            started_at: 160,
            target_quantity: 120,
            production_location_name: 150,
            actions: 150
        };
    });

    useEffect(() => {
        localStorage.setItem('productionColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchProductionLots() {
            try {
                setLoading(true)
                const response = await productionAPI.getAll(currentPage, pageSize, statusFilter, searchTerm)
                setProductionLots(response.results || [])
                setTotalPages(response.count || 0)
            } finally {
                setLoading(false)
            }
        }
        fetchProductionLots()
    }, [currentPage, pageSize, searchTerm, statusFilter])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'in_progress':
                return <Badge bg="primary">In Progress</Badge>
            case 'completed':
                return <Badge bg="success">Completed</Badge>
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>
            default:
                return <Badge bg="light" text="dark">{status}</Badge>
        }
    }

    const formatDateTime = (dateString) => {
        if (!dateString) return '-'
        const date = new Date(dateString)
        return date.toLocaleString()
    }

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Production LOTs</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by lot number or recipe..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <ButtonGroup>
                        <Button
                            variant={statusFilter === 'in_progress' ? 'primary' : 'outline-primary'}
                            onClick={() => {
                                setStatusFilter('in_progress');
                                setCurrentPage(1);
                            }}
                        >
                            In Progress
                        </Button>
                        <Button
                            variant={statusFilter === 'completed' ? 'success' : 'outline-success'}
                            onClick={() => {
                                setStatusFilter('completed');
                                setCurrentPage(1);
                            }}
                        >
                            Completed
                        </Button>
                        <Button
                            variant={statusFilter === 'cancelled' ? 'secondary' : 'outline-secondary'}
                            onClick={() => {
                                setStatusFilter('cancelled');
                                setCurrentPage(1);
                            }}
                        >
                            Cancelled
                        </Button>
                        <Button
                            variant={statusFilter === '' ? 'dark' : 'outline-dark'}
                            onClick={() => {
                                setStatusFilter('');
                                setCurrentPage(1);
                            }}
                        >
                            All
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="production_table"
                        columns={[
                            {
                                id: 'lot_number',
                                header: 'LOT Number',
                                cell: (row) => highlightText(row.lot_number, searchTerm)
                            },
                            {
                                id: 'recipe_name',
                                header: 'Recipe',
                                cell: (row) => highlightText(row.recipe_name || '-', searchTerm)
                            },
                            {
                                id: 'status',
                                header: 'Status',
                                cell: (row) => getStatusBadge(row.status)
                            },
                            {
                                id: 'started_at',
                                header: 'Started',
                                cell: (row) => formatDateTime(row.started_at)
                            },
                            {
                                id: 'target_quantity',
                                header: 'Target Qty',
                                cell: (row) => row.target_quantity
                            },
                            {
                                id: 'production_location_name',
                                header: 'Location',
                                cell: (row) => row.production_location_name || '-'
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                navigate(`/production/${row._id}`)
                                            }}
                                        >
                                            View
                                        </Button>
                                        {row.status === 'in_progress' && hasPermission('production.finalize') && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/production/${row._id}/finalize`)
                                                }}
                                            >
                                                Finalize
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={productionLots}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/production/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default ProductionListScreen
